import { useCallback, useEffect, useState } from "react";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePostDocumentDownloadToken } from "@/hooks/useFiles";
import { absoluteUrl } from "@/utils/common.utils";
import type { DealDocument } from "@/types/deals.types";

/** What a browser can show inline. Word, Excel and HEIC download instead. */
const PREVIEWABLE = /^(application\/pdf|image\/(jpeg|png|webp|gif)|text\/(plain|csv))$/;

/** The document's name with its extension, which the stored name drops. */
export function documentFilename(doc: Pick<DealDocument, "name" | "storage_path">): string {
  const name = (doc.name || "document").trim() || "document";
  const ext = /\.([a-z0-9]+)$/i.exec(doc.storage_path || "")?.[1]?.toLowerCase();
  return ext && !name.toLowerCase().endsWith(`.${ext}`) ? `${name}.${ext}` : name;
}

function saveBlobUrl(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

interface Preview {
  doc: DealDocument;
  url: string;
  type: string;
}

/**
 * Opens a deal document in the app.
 *
 * Opening the signed download URL in a new tab was blocked as a pop-up (it
 * runs after the token request, outside the click) and always downloaded. The
 * file is fetched here instead: PDFs, images and text show in a dialog with a
 * Download button; anything a browser cannot render is saved under its real
 * name. Linked documents (a pasted URL) still open in a new tab.
 */
export function useDocumentViewer() {
  const { mutateAsync: getToken } = usePostDocumentDownloadToken();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const openDocument = useCallback(async (doc: DealDocument) => {
    if (!doc.storage_path) {
      if (doc.url) window.open(doc.url, "_blank", "noopener,noreferrer");
      return;
    }
    setLoadingId(doc.id);
    try {
      const res = await getToken(doc.id);
      if (!res.data?.url) return;
      const response = await fetch(absoluteUrl(res.data.url));
      if (!response.ok) throw new Error("This document could not be opened.");
      const blob = await response.blob();
      const type = (doc.content_type || blob.type || "").toLowerCase();
      const url = URL.createObjectURL(type && blob.type !== type ? new Blob([blob], { type }) : blob);
      if (PREVIEWABLE.test(type)) {
        setPreview({ doc, url, type });
      } else {
        saveBlobUrl(url, documentFilename(doc));
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      }
    } catch (err) {
      // The token request already toasts its own failures.
      if (err instanceof Error && !(err as { isAxiosError?: boolean }).isAxiosError) toast.error(err.message);
    } finally {
      setLoadingId(null);
    }
  }, [getToken]);

  const viewer = (
    <Dialog open={Boolean(preview)} onOpenChange={(open) => { if (!open) setPreview(null); }}>
      <DialogContent className="flex h-[88vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-2xl border-dash-border bg-white p-0 font-dashboard" data-testid="document-viewer">
        {preview && (
          <>
            <DialogHeader className="flex-row items-center justify-between gap-4 space-y-0 border-b border-dash-border px-5 py-4 pr-14">
              <div className="min-w-0">
                <DialogTitle className="truncate text-lg text-dash-ink">{preview.doc.name}</DialogTitle>
                <DialogDescription className="text-xs uppercase tracking-wider text-dash-faint">{preview.doc.category}</DialogDescription>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button type="button" variant="dashboardOutline" size="sm" onClick={() => window.open(preview.url, "_blank", "noopener")} data-testid="document-viewer-new-tab">
                  <ExternalLink data-icon="inline-start" /> Open in new tab
                </Button>
                <Button type="button" variant="dashboardPrimary" size="sm" onClick={() => saveBlobUrl(preview.url, documentFilename(preview.doc))} data-testid="document-viewer-download">
                  <Download data-icon="inline-start" /> Download
                </Button>
              </div>
            </DialogHeader>
            <div className="min-h-0 flex-1 bg-dash-searchBg">
              {preview.type.startsWith("image/") ? (
                <img src={preview.url} alt={preview.doc.name} className="mx-auto h-full max-w-full object-contain" />
              ) : (
                <iframe src={preview.url} title={preview.doc.name} className="size-full border-0 bg-white" />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  return { openDocument, viewer, loadingId };
}
